import { Stack, Theme, useTheme } from '@mui/material';
import { ServiceLevel } from '@provision/service-level-utils';
import { useMemo } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { getServiceLevelColors } from 'src/graphql';
import {
  GetStripeSubscriptionDetailsResponse,
  SubscriptionStatus,
} from 'src/graphql/entities/stripe-subscription-details';
import {
  addZeroTime,
  formatDateToLocalString,
  getAmount,
  getSubscriptionStatusColor,
  OPTIONS_LOCALE,
} from './subscription-details.utils';

function getSubscriptionStatusTooltipText(status: SubscriptionStatus) {
  const statusesTexting = {
    [SubscriptionStatus.Active]: '',
    [SubscriptionStatus['Past Due']]:
      "The most recent payment attempt failed. Please use the payment portal to update the client's CC information.",
    [SubscriptionStatus.Unpaid]:
      "We have not been able to process a payment for over 30 days. Please use the payment portal to update the client's CC information.",
    [SubscriptionStatus.Canceled]:
      'Membership is no longer active. Please use the Client Activation Flow to activate a new membership if desired.',
    [SubscriptionStatus.Incomplete]:
      "The initial activation attempt failed. Please use the payment portal to update the client's CC information.",
    [SubscriptionStatus['Incomplete Expired']]:
      "The initial activation attempt failed. Please use the payment portal to update the client's CC information.",
    [SubscriptionStatus.Trialing]:
      'The subscription is still in trial mode and will flip to Active upon the trial ending as long as we have a payment method on file.',
  };

  return statusesTexting?.[status] || '';
}

function getPaymentMethodText(
  subscriptionDetails: GetStripeSubscriptionDetailsResponse | undefined,
) {
  if (subscriptionDetails?.paymentMethod) {
    const paymentMethod = subscriptionDetails.paymentMethod;

    return paymentMethod.type === 'card'
      ? `${paymentMethod.brand} ${paymentMethod.last4} - ${paymentMethod.expMonth}/${paymentMethod.expYear}`
      : `${paymentMethod.bankName} - ${paymentMethod.last4}`;
  }

  return 'Not found';
}

function getPausedSubscriptionText(
  subscriptionDetails: GetStripeSubscriptionDetailsResponse | undefined,
) {
  return `This membership currently has a subscription that is paused.${
    subscriptionDetails?.resumesAt
      ? ` It will unpause on ${formatDateToLocalString({
          date: addZeroTime(subscriptionDetails?.resumesAt),
          options: OPTIONS_LOCALE,
        })}.`
      : ''
  }`;
}

function getCancelsAtText(date: string) {
  return `This subscription is set to cancel on ${formatDateToLocalString({
    date: addZeroTime(date),
    options: OPTIONS_LOCALE,
  })}. Upon cancellation the service level will downgrade to Non-Member.`;
}

function getCouponTooltipText(coupon?: GetStripeSubscriptionDetailsResponse['coupon']) {
  if (coupon?.amountOff) {
    if (coupon.duration === 'repeating') {
      return `${getAmount(coupon.amountOff)} off, ${coupon.duration} for ${
        coupon.durationInMonths
      } months`;
    }
    return `${getAmount(coupon.amountOff)} off, ${coupon.duration}`;
  }
  if (coupon?.duration === 'repeating') {
    return `${coupon.percentOff}% off, ${coupon.duration} for ${coupon.durationInMonths} months`;
  }
  return `${coupon?.percentOff}% off, ${coupon?.duration}`;
}

function getBalanceColor(balance: number, theme: Theme) {
  if (balance < 0) {
    return theme.palette.success.main;
  }

  if (balance > 0) {
    return theme.palette.error.main;
  }

  return undefined;
}

export const useSubscriptionDetailsSummaryFields = ({
  subscriptionDetails,
}: {
  subscriptionDetails?: GetStripeSubscriptionDetailsResponse;
}) => {
  const theme = useTheme();

  const fields = useMemo(() => {
    return [
      {
        id: 'plan',
        label: 'Plan',
        value: subscriptionDetails?.plan || 'Not Found',
        disabled: !subscriptionDetails?.plan,
        icon: (
          <DynamicIcon
            name="circle"
            filled
            color={
              getServiceLevelColors((subscriptionDetails?.plan || '') as ServiceLevel, theme)
                .color
            }
          />
        ),
        endAdornment: (
          <Stack direction="row">
            {subscriptionDetails?.paused ? (
              <Tooltip
                title={getPausedSubscriptionText(subscriptionDetails)}
                sx={{ cursor: 'default' }}
              >
                <DynamicIcon name="pause-circle" library="lucide" />
              </Tooltip>
            ) : undefined}
            {subscriptionDetails?.cancelsAt ? (
              <Tooltip
                title={getCancelsAtText(subscriptionDetails.cancelsAt)}
                sx={{ cursor: 'default' }}
              >
                <DynamicIcon name="calendar-x" library="lucide" />
              </Tooltip>
            ) : undefined}
          </Stack>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        value: subscriptionDetails?.subscriptionStatus || 'Not Found',
        disabled: !subscriptionDetails?.subscriptionStatus,
        icon: (
          <DynamicIcon
            name="circle"
            filled
            color={getSubscriptionStatusColor(
              (subscriptionDetails?.subscriptionStatus || '') as SubscriptionStatus,
              theme,
            )}
          />
        ),
        endAdornment:
          subscriptionDetails?.subscriptionStatus !== SubscriptionStatus.Active ? (
            <Tooltip
              title={getSubscriptionStatusTooltipText(
                subscriptionDetails?.subscriptionStatus as SubscriptionStatus,
              )}
            >
              <DynamicIcon name="info" library="lucide" sx={{ cursor: 'default' }} />
            </Tooltip>
          ) : undefined,
      },
      {
        id: 'paymentMethod',
        label: 'Payment Method',
        value: getPaymentMethodText(subscriptionDetails),
        disabled: !subscriptionDetails?.paymentMethod,
        icon: (
          <DynamicIcon
            library="lucide"
            name={
              subscriptionDetails?.paymentMethod?.type === 'card' ? 'credit-card' : 'landmark'
            }
          />
        ),
      },
      {
        id: 'coupon',
        label: 'Coupon',
        value: subscriptionDetails?.coupon?.name || 'Not Found',
        disabled: !subscriptionDetails?.coupon?.name,
        icon: <DynamicIcon name="tag" library="lucide" />,
        endAdornment: subscriptionDetails?.coupon?.name ? (
          <Tooltip title={getCouponTooltipText(subscriptionDetails?.coupon)}>
            <DynamicIcon name="info" library="lucide" sx={{ cursor: 'default' }} />
          </Tooltip>
        ) : undefined,
      },
      {
        id: 'renewalDate',
        label: 'Renewal Date',
        value: subscriptionDetails?.renewalDate
          ? formatDateToLocalString({
              date: addZeroTime(subscriptionDetails?.renewalDate || ''),
              options: OPTIONS_LOCALE,
            })
          : 'Not Found',
        disabled: !subscriptionDetails?.renewalDate,
        icon: <DynamicIcon name="calendar" library="lucide" />,
      },
      {
        id: 'balance',
        label: 'Balance',
        value: getAmount(subscriptionDetails?.balance),
        color: getBalanceColor(subscriptionDetails?.balance || 0, theme),
        disabled: !subscriptionDetails?.balance,
        tooltip:
          Number(subscriptionDetails?.balance || 0) < 0
            ? 'The customer currently has a credit that will be applied towards their subscription'
            : '',
        icon: <DynamicIcon name="wallet" library="lucide" />,
      },
      {
        id: 'renewalAmount',
        label: 'Renewal Amount',
        value: getAmount(subscriptionDetails?.renewalAmount),
        disabled: !subscriptionDetails?.renewalAmount,
        icon: <DynamicIcon name="receipt" library="lucide" />,
      },
      {
        id: 'billingFrequency',
        label: 'Billing Frequency',
        value: subscriptionDetails?.billingFrequency || 'Not Found',
        disabled: !subscriptionDetails?.billingFrequency,
        icon: <DynamicIcon name="rotate-cw" library="lucide" />,
      },
      {
        id: 'trialEnd',
        label: 'Trial End',
        value: subscriptionDetails?.trialEnd
          ? formatDateToLocalString({
              date: addZeroTime(subscriptionDetails?.trialEnd || ''),
              options: OPTIONS_LOCALE,
            })
          : 'Not Found',
        disabled: !subscriptionDetails?.trialEnd,
        icon: <DynamicIcon name="calendar-x" library="lucide" />,
      },
      ...(subscriptionDetails?.paused && subscriptionDetails?.resumesAt
        ? [
            {
              id: 'pausedUntil',
              label: 'Paused Until',
              value: formatDateToLocalString({
                date: addZeroTime(subscriptionDetails.resumesAt),
                options: OPTIONS_LOCALE,
              }),
              disabled: false,
              icon: <DynamicIcon name="pause-circle" library="lucide" />,
            },
          ]
        : []),
    ];
  }, [subscriptionDetails, theme]);

  return {
    fields: subscriptionDetails ? fields : [],
  };
};
